import type { NostrEvent } from '@nostrify/nostrify';

/**
 * NostrBoard uses a single addressable event kind (32323) for the entire forum
 * hierarchy. Every node carries a unique `d` tag, an optional `i` tag that
 * points at its parent's `d` tag, and a `t` tag holding its display title.
 *
 * Hierarchy (derived from the `i` reference chain):
 *   category  (no parent)
 *     forum   (i -> category)
 *       thread (first message; i -> forum)
 *         reply (i -> thread)
 *
 * See NIP.md for the full schema description.
 */
export const FORUM_KIND = 32323;

export type ForumNodeType = 'category' | 'forum' | 'thread' | 'reply' | 'orphan';

export interface ForumNode {
  /** The `d` tag value (globally unique by construction). */
  id: string;
  pubkey: string;
  /** First `t` tag, or '' for replies that have no title. */
  title: string;
  content: string;
  /** Parent `d` tag from the `i` tag, or null for top-level categories. */
  parent: string | null;
  createdAt: number;
  event: NostrEvent;
}

type IdPrefix = 'cat' | 'forum' | 'thread' | 'msg';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/** Generate a (practically) globally-unique d-tag so `i` references never collide. */
export function genId(prefix: IdPrefix): string {
  const len = 14;
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < len; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return `${prefix}_${out}`;
}

export function parseNode(event: NostrEvent): ForumNode {
  const d = event.tags.find((t) => t[0] === 'd')?.[1] ?? '';
  const i = event.tags.find((t) => t[0] === 'i')?.[1];
  const t = event.tags.find((t) => t[0] === 't')?.[1] ?? '';
  return {
    id: d,
    pubkey: event.pubkey,
    title: t,
    content: event.content ?? '',
    parent: i ?? null,
    createdAt: event.created_at,
    event,
  };
}

export interface ForumTree {
  nodes: ForumNode[];
  byId: Map<string, ForumNode>;
}

/**
 * Build the forum tree from raw events. Addressable events are de-duplicated
 * per (pubkey + d), keeping the most recent version.
 */
export function buildTree(events: NostrEvent[]): ForumTree {
  const latest = new Map<string, ForumNode>();
  for (const ev of events) {
    const node = parseNode(ev);
    if (!node.id) continue;
    const key = `${node.pubkey}:${node.id}`;
    const existing = latest.get(key);
    if (!existing || node.createdAt > existing.createdAt) latest.set(key, node);
  }
  const nodes = [...latest.values()];
  const byId = new Map<string, ForumNode>();
  for (const n of nodes) byId.set(n.id, n);
  return { nodes, byId };
}

/** Depth in the hierarchy: 0=category, 1=forum, 2=thread, >=3=reply. */
export function depthOf(node: ForumNode, byId: Map<string, ForumNode>): number {
  if (!node.parent) return 0;
  const seen = new Set<string>();
  let cur: ForumNode | undefined = node;
  let depth = 0;
  while (cur && cur.parent && !seen.has(cur.id)) {
    seen.add(cur.id);
    const parent = byId.get(cur.parent);
    if (!parent) {
      depth += 1;
      break;
    }
    depth += 1;
    cur = parent;
  }
  return depth;
}

export function nodeType(node: ForumNode, byId: Map<string, ForumNode>): ForumNodeType {
  const depth = depthOf(node, byId);
  if (depth <= 0) return 'category';
  if (depth === 1) return 'forum';
  if (depth === 2) return 'thread';
  return 'reply';
}

/** All direct children of a node (events whose `i` tag equals `parentId`). */
export function childrenOf(byId: Map<string, ForumNode>, parentId: string): ForumNode[] {
  const out: ForumNode[] = [];
  for (const n of byId.values()) {
    if (n.parent === parentId) out.push(n);
  }
  return out;
}

export interface ForumStats {
  topics: number;
  posts: number;
  lastPost?: ForumNode;
}

export function forumStats(byId: Map<string, ForumNode>, forumId: string): ForumStats {
  const threads = childrenOf(byId, forumId);
  let replies = 0;
  let lastPost: ForumNode | undefined;
  for (const thread of threads) {
    const rs = childrenOf(byId, thread.id);
    replies += rs.length;
    for (const node of [thread, ...rs]) {
      if (!lastPost || node.createdAt > lastPost.createdAt) lastPost = node;
    }
  }
  return { topics: threads.length, posts: threads.length + replies, lastPost };
}

export interface ThreadStats {
  replies: number;
  lastPost?: ForumNode;
}

export function threadStats(byId: Map<string, ForumNode>, threadId: string): ThreadStats {
  const replies = childrenOf(byId, threadId);
  let lastPost: ForumNode | undefined = byId.get(threadId);
  for (const r of replies) {
    if (!lastPost || r.createdAt > lastPost.createdAt) lastPost = r;
  }
  return { replies: replies.length, lastPost };
}

/** Sort threads by most recent activity (last post) descending. */
export function sortThreadsByActivity(byId: Map<string, ForumNode>, threads: ForumNode[]): ForumNode[] {
  return [...threads].sort((a, b) => {
    const la = threadStats(byId, a.id).lastPost?.createdAt ?? a.createdAt;
    const lb = threadStats(byId, b.id).lastPost?.createdAt ?? b.createdAt;
    return lb - la;
  });
}

/**
 * Only allow http(s) URLs. Returns null for anything unsafe so it can be
 * skipped or rendered as plain text. This is critical because Nostr content
 * is untrusted and could contain `javascript:` or other malicious URLs.
 */
export function safeUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol === 'http:' || url.protocol === 'https:') return url.href;
    return null;
  } catch {
    return null;
  }
}

export function timeAgo(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

export function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleString();
}
